import React, {useState} from 'react';
import {GameUiRootContext} from '../../gameUi/GameUiPortal';
import './AdminGameUiKitPage.css';
import '../../../styles/gameUi/tokens.css';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {GamePanel, GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES} from '../../gameUi/GamePanel';
import {GameButton, GAME_BUTTON_SIZES, GAME_BUTTON_VARIANTS} from '../../gameUi/GameButton';
import {HexButton} from '../../gameUi/HexButton';
import {StatWell} from '../../gameUi/StatWell';
import {PlayerFrame} from '../../gameUi/PlayerFrame';
import {EngravedIcon} from '../../gameUi/EngravedIcon';
import {GAME_UI_PHASES} from '../../../utils/gameUi/getGameUiPhase';
import {COLORS_CONFIG, PLAYER_COLORS} from '../../../configs/colors';
import {GROUND_BACKGROUND_IMAGES, GROUND_BACKGROUNDS} from '../../../configs/groundBackgrounds';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {ToastView} from '../../Toast/Toast';
import {ReactionToastView} from '../../ReactionToast/ReactionToast';
import PolicyPurchaseToast from '../../PolicyPurchaseToast/PolicyPurchaseToast';
import '../../MatchPolicyToast/MatchPolicyToast.css';
import {getReactionTypeConfig} from '../../../configs/reactions';
import {POLICY_CONFIGS} from '../../../configs/policies';
import {POLICIES} from '../../../constants/policies';
import {TOAST_ICONS, TOAST_TYPES} from '../../../constants/toast';
import {WAR_PHASE_STAGES} from '../../../constants/phases';
import {getWarPhaseStageName} from '../../../utils/phase/getWarPhaseStageName';
import AdminGameUiKitMatchScene, {KIT_HEADER_VIEWS, KitFooterPlate} from './AdminGameUiKitMatchScene';
import {getKitCompleteButtonSamples, KitCompleteButton} from './KitCompleteButtons';

const PAGE_ID = 'AdminGameUiKitPage';

const PHASE_OPTIONS = [
  {id: GAME_UI_PHASES.PREP, label: 'Подготовка', icon: '/img/icons/flag white.png'},
  {id: GAME_UI_PHASES.SUPPLY, label: 'Снабжение', icon: '/img/icons/supply white.png'},
  {id: GAME_UI_PHASES.CONSTRUCTION, label: 'Строительство', icon: '/img/icons/hammer white.png'},
  {id: GAME_UI_PHASES.WAR, label: 'Война', icon: '/img/icons/shooting white.png'},
  {id: GAME_UI_PHASES.END, label: 'Конец игры', icon: '/img/icons/win points white.png'},
];

// Цвета игроков из партии; нейтрал и зритель — не игроки, им «моего» не бывает
const PLAYER_COLOR_OPTIONS = Object.values(PLAYER_COLORS)
  .filter((color) => color !== PLAYER_COLORS.NEUTRAL && color !== PLAYER_COLORS.SPECTATOR_COLOR);

const GROUND_OPTIONS = [
  GROUND_BACKGROUNDS.GREEN_1,
  GROUND_BACKGROUNDS.SALT_1,
  GROUND_BACKGROUNDS.VOLCANO_1,
  GROUND_BACKGROUNDS.WINTER_1,
];

const HEX_TOKENS = [
  {src: '/img/hexes/barracks.efab12f7.png', label: 'Казарма', price: 3},
  {src: '/img/hexes/farm.699e6dfc.png', label: 'Ферма', price: 2},
  {src: '/img/hexes/mine.ecae90cd.png', label: 'Рудник', price: 4},
  {src: '/img/hexes/quarter.f69660fa.png', label: 'Квартал', price: 5},
  {src: '/img/hexes/road.1f281346.png', label: 'Дорога', price: 9, disabled: true},
];

const PORTRAIT = '/img/factions/dwarves.jpg';

// Тосты матча во всех вариантах: обычный (язычок цвета типа со значком типа или своей
// иконкой события), реакция, покупка политики
const TOAST_SAMPLES = [
  {type: TOAST_TYPES.INFO, label: 'Информация', message: 'Ход игрока Гном Торин'},
  {
    type: TOAST_TYPES.INFO,
    label: 'Информация, длинный',
    message: 'Ловушка сработала: отряд противника потерял 2 здоровья.\nСледующая ловушка — через раунд',
  },
  {
    type: TOAST_TYPES.INFO,
    label: 'Подсказка новичку',
    message: 'Выберите одну из выделенных казарм для размещения выбранного отряда.',
    isHint: true,
  },
  {type: TOAST_TYPES.SUCCESS, label: 'Успех', message: 'Город захвачен'},
  {type: TOAST_TYPES.WARNING, label: 'Предупреждение', message: 'Недостаточно снабжения для всех отрядов'},
  {type: TOAST_TYPES.ERROR, label: 'Ошибка', message: 'Нет связи с матчем — сообщение не отправлено'},
  {type: TOAST_TYPES.SUCCESS, icon: TOAST_ICONS.INITIATIVE, label: 'Перехват инициативы', message: 'Вы перехватили инициативу!'},
  {type: TOAST_TYPES.ERROR, icon: TOAST_ICONS.STEAL, label: 'Кража монет', message: 'Торин украл у Вас 2 монеты!'},
  {type: TOAST_TYPES.ERROR, icon: TOAST_ICONS.TRAP, label: 'Ловушка', message: 'Ваш отряд попал в ловушку гоблинов!'},
  {type: TOAST_TYPES.SUCCESS, icon: TOAST_ICONS.COINS, label: 'Обмен на монеты', message: 'Вы обменяли 4 снабжения на 2 монеты.'},
];

const getSamplePlayers = (playerColor) => ({
  me: {id: 'kit-me', name: 'Гном Торин', color: playerColor, factionId: 'dwarves'},
  other: {
    id: 'kit-other',
    name: 'Королева Улья',
    color: playerColor === PLAYER_COLORS.RED ? PLAYER_COLORS.BLUE : PLAYER_COLORS.RED,
    factionId: 'insectoids',
  },
});

const REACTION_SAMPLES = [
  {label: 'Реакция всем', reactionId: 'greeting', from: 'other'},
  {label: 'Реакция игроку', reactionId: 'mockery', from: 'other', to: 'me'},
  {label: 'Своя реакция', reactionId: 'trade_offer', from: 'me', to: 'other', isOwn: true},
];

const SAMPLE_POLICY = POLICY_CONFIGS.find((config) => config.id === POLICIES.AGRARIANISM);
const WAR_STAGE_OPTIONS = Object.values(WAR_PHASE_STAGES).map((stage) => ({
  id: stage,
  label: getWarPhaseStageName(stage),
}));

const HEADER_VIEW_OPTIONS = [
  {id: KIT_HEADER_VIEWS.OWN_TURN, label: 'Свой ход'},
  {id: KIT_HEADER_VIEWS.OTHER_TURN, label: 'Чужой ход'},
  {id: KIT_HEADER_VIEWS.SPECTATOR, label: 'Зритель'},
];

const Section = ({title, children}) => (
  <section className={'admin-game-ui-kit_section'}>
    <h2 className={'admin-game-ui-kit_section-title'}>{title}</h2>
    <div className={'admin-game-ui-kit_row'}>{children}</div>
  </section>
);

const Caption = ({label, children}) => (
  <div className={'admin-game-ui-kit_caption'}>
    {children}
    <span>{label}</span>
  </div>
);

/**
 * Витрина дизайн-системы матча (openspec/changes/game-ui-design-system):
 * все примитивы во всех состояниях на настоящей карте, с выбором фазы и
 * цвета игрока. По ней ревьюится каждый этап перевода интерфейса матча
 */
function AdminGameUiKitPage({onBack}) {
  usePageAnalytics(PAGE_ID);
  const [phase, setPhase] = useState(GAME_UI_PHASES.WAR);
  const [playerColor, setPlayerColor] = useState(PLAYER_COLORS.YELLOW);
  const [ground, setGround] = useState(GROUND_BACKGROUNDS.GREEN_1);
  const [selectedToken, setSelectedToken] = useState(0);
  const [warStage, setWarStage] = useState(WAR_PHASE_STAGES.SHOOTING);
  const [headerView, setHeaderView] = useState(KIT_HEADER_VIEWS.OWN_TURN);
  // Корень порталов, как у экрана матча: выпадающие из шапки панели (статы
  // игрока) рендерятся в него, а не в body — со стилями дизайн-системы
  const [stageRoot, setStageRoot] = useState(null);

  const samplePlayers = getSamplePlayers(playerColor);

  return (
    <>
      <h1 className="lobby-page-title">Витрина интерфейса матча</h1>

      <div className={'admin-game-ui-kit_controls'}>
        <div className={'admin-game-ui-kit_control'}>
          <span>Фаза</span>
          <div className={'admin-game-ui-kit_options'}>
            {PHASE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type={'button'}
                className={'admin-game-ui-kit_option'}
                aria-pressed={phase === option.id}
                onClick={() => setPhase(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {phase === GAME_UI_PHASES.WAR && (
          <div className={'admin-game-ui-kit_control'}>
            <span>Этап войны</span>
            <div className={'admin-game-ui-kit_options'}>
              {WAR_STAGE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type={'button'}
                  className={'admin-game-ui-kit_option'}
                  aria-pressed={warStage === option.id}
                  onClick={() => setWarStage(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={'admin-game-ui-kit_control'}>
          <span>Шапка</span>
          <div className={'admin-game-ui-kit_options'}>
            {HEADER_VIEW_OPTIONS.map((option) => (
              <button
                key={option.id}
                type={'button'}
                className={'admin-game-ui-kit_option'}
                aria-pressed={headerView === option.id}
                onClick={() => setHeaderView(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className={'admin-game-ui-kit_control'}>
          <span>Цвет игрока</span>
          <div className={'admin-game-ui-kit_options'}>
            {PLAYER_COLOR_OPTIONS.map((color) => (
              <button
                key={color}
                type={'button'}
                className={'admin-game-ui-kit_swatch'}
                title={color}
                aria-label={color}
                aria-pressed={playerColor === color}
                style={{backgroundColor: COLORS_CONFIG[color]}}
                onClick={() => setPlayerColor(color)}
              />
            ))}
          </div>
        </div>
        <div className={'admin-game-ui-kit_control'}>
          <span>Карта</span>
          <div className={'admin-game-ui-kit_options'}>
            {GROUND_OPTIONS.map((option) => (
              <button
                key={option}
                type={'button'}
                className={'admin-game-ui-kit_option'}
                aria-pressed={ground === option}
                onClick={() => setGround(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        ref={setStageRoot}
        className={'game-ui admin-game-ui-kit_stage'}
        data-gui-phase={phase}
        style={{backgroundImage: `url("${GROUND_BACKGROUND_IMAGES[ground]}")`}}
      >
        <Section title={'Сцена: шапка, футер, кнопка хода'}>
          <GameUiRootContext.Provider value={stageRoot}>
            <AdminGameUiKitMatchScene
              phase={phase}
              warStage={warStage}
              playerColor={playerColor}
              headerView={headerView}
            />
          </GameUiRootContext.Provider>
        </Section>

        <Section title={'Кнопка хода: все состояния фазы и этапа'}>
          {getKitCompleteButtonSamples(phase, warStage).map((sample) => (
            <Caption key={sample.id} label={sample.label}>
              <KitCompleteButton sample={sample} me={samplePlayers.me} rival={samplePlayers.other}/>
            </Caption>
          ))}
        </Section>

        <Section title={'Футер в одну строку (чужой ход, ожидание)'}>
          <KitFooterPlate title={'Ход игрока Тень Мэриэль'}/>
        </Section>

        <Section title={'Кнопки'}>
          <Caption label={'«Мое»'}>
            <GameButton variant={GAME_BUTTON_VARIANTS.MINE} playerColor={playerColor}>Завершить этап</GameButton>
          </Caption>
          <Caption label={'Цвет фазы'}>
            <GameButton variant={GAME_BUTTON_VARIANTS.PHASE}>Фаза</GameButton>
          </Caption>
          <Caption label={'Обычная'}>
            <GameButton>Переброс</GameButton>
          </Caption>
          <Caption label={'Нажата'}>
            <GameButton pressed>Переброс</GameButton>
          </Caption>
          <Caption label={'Недоступна'}>
            <GameButton disabled>Переброс</GameButton>
          </Caption>
          <Caption label={'«Мое», недоступна'}>
            <GameButton variant={GAME_BUTTON_VARIANTS.MINE} playerColor={playerColor} disabled>Ход</GameButton>
          </Caption>
          <Caption label={'Иконка'}>
            <GameButton size={GAME_BUTTON_SIZES.ICON} aria-label={'Чат'}>
              <EngravedIcon src={'/img/icons/chat.png'} size={26}/>
            </GameButton>
          </Caption>
          <Caption label={'Утопленная'}>
            <GameButton variant={GAME_BUTTON_VARIANTS.SUNK} size={GAME_BUTTON_SIZES.ICON} aria-label={'Отменить'}>
              <EngravedIcon src={'/img/icons/return.png'} size={22}/>
            </GameButton>
          </Caption>
          <div className={'admin-game-ui-kit_wide'}>
            <Caption label={'На всю ширину'}>
              <GameButton size={GAME_BUTTON_SIZES.WIDE}>
                <EngravedIcon src={'/img/icons/rules_white.png'} size={22}/>Правила
              </GameButton>
            </Caption>
          </div>
        </Section>

        <Section title={'Жетоны-кнопки'}>
          {HEX_TOKENS.map((token, index) => (
            <Caption key={token.label} label={`${token.label} · ${token.price}`}>
              <HexButton
                src={token.src}
                alt={token.label}
                selected={selectedToken === index}
                disabled={token.disabled}
                playerColor={playerColor}
                onClick={() => setSelectedToken(index)}
              />
            </Caption>
          ))}
          <Caption label={'Каменный гекс'}>
            <HexButton alt={'Отряд'} width={60}>
              <EngravedIcon src={'/img/icons/unit d8 white.png'} size={30} color={COLORS_CONFIG[playerColor]}/>
            </HexButton>
          </Caption>
        </Section>

        <Section title={'Плиты'}>
          <Caption label={'Лист'}><GamePanel className={'admin-game-ui-kit_swatch-plate'}/></Caption>
          <Caption label={'Полоса'}>
            <GamePanel material={GAME_PANEL_MATERIALS.BAR} className={'admin-game-ui-kit_swatch-plate'}/>
          </Caption>
          <Caption label={'Фаза'}>
            <GamePanel material={GAME_PANEL_MATERIALS.PHASE} className={'admin-game-ui-kit_swatch-plate'}/>
          </Caption>
          <Caption label={'«Мое»'}>
            <GamePanel material={GAME_PANEL_MATERIALS.MINE} playerColor={playerColor}
                       className={'admin-game-ui-kit_swatch-plate'}/>
          </Caption>
          <Caption label={'Числа над полем'}>
            <GamePanel material={GAME_PANEL_MATERIALS.BONE} shape={GAME_PANEL_SHAPES.S}
                       className={'admin-game-ui-kit_bone'}>
              <img src={'/img/icons/unit d6 friend.png'} alt={''}/>1
            </GamePanel>
          </Caption>
        </Section>

        <Section title={'Ресурсы и портреты'}>
          <StatWell icon={'/img/icons/coins.png'} value={6} income={5}/>
          <StatWell icon={'/img/icons/win points.png'} value={12} income={2}/>
          <StatWell icon={'/img/icons/action.png'} maskIcon value={'2/3'}/>
          <PlayerFrame src={PORTRAIT} playerColor={playerColor}/>
          <PlayerFrame src={'/img/factions/insectoids.jpg'} playerColor={PLAYER_COLORS.BLUE}/>
        </Section>

        <Section title={'Тосты'}>
          <div className={'admin-game-ui-kit_toasts'}>
            {TOAST_SAMPLES.map((sample) => (
              <Caption key={sample.label} label={sample.label}>
                <ToastView
                  type={sample.type}
                  icon={sample.icon}
                  message={sample.message}
                  onClose={() => {}}
                  onHideHint={sample.isHint ? () => {} : undefined}
                />
              </Caption>
            ))}
            {REACTION_SAMPLES.map((sample) => (
              <Caption key={sample.label} label={sample.label}>
                <ReactionToastView
                  fromPlayer={samplePlayers[sample.from]}
                  toPlayer={sample.to ? samplePlayers[sample.to] : null}
                  reactionConfig={getReactionTypeConfig(sample.reactionId)}
                  isOwnReaction={Boolean(sample.isOwn)}
                  onClick={() => {}}
                />
              </Caption>
            ))}
            {[samplePlayers.me, samplePlayers.other].map((player) => (
              <Caption key={player.id} label={`Покупка политики · ${player.color}`}>
                <div className={'toast-shadow'}>
                  <PolicyPurchaseToast
                    className={'match-policy-toast match-policy-toast_visible'}
                    player={player}
                    policy={SAMPLE_POLICY}
                  />
                </div>
              </Caption>
            ))}
          </div>
        </Section>

        <Section title={'Все цвета игроков: кнопка хода'}>
          {PLAYER_COLOR_OPTIONS.map((color) => (
            <div key={color} className={'admin-game-ui-kit_caption'} style={getPlayerColorStyle(color)}>
              <GameButton variant={GAME_BUTTON_VARIANTS.MINE} playerColor={color} onClick={() => setPlayerColor(color)}>
                <EngravedIcon src={'/img/icons/check.png'} size={20}/>Ход
              </GameButton>
              <span>{color}</span>
            </div>
          ))}
        </Section>
      </div>

      <div className="main-menu-buttons admin-game-ui-kit_page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default AdminGameUiKitPage;
